/**
 * Copy pools and field-aware status text for ToolActivityStatus.
 * Centralized here so copy can be tuned without touching component code.
 */

/**
 * Warm copy shown in the bottom ThinkingIndicator while waiting for TTFB.
 * These are broader/aspirational — the user hasn't acted yet, we're just processing.
 */
export const THINKING_COPY_POOL = [
  "Personalizing your experience...",
  "Building your plan...",
  "Connecting the dots...",
  "Tailoring your plan...",
  "Learning what works for you...",
  "Getting things ready for you...",
] as const;

/** Interval between rotating the bottom ThinkingIndicator copy (ms). */
export const THINKING_COPY_ROTATE_MS = 2500;

/** Warm rotating copy shown as follow-up action during tool execution. */
export const WARM_COPY_POOL = [
  "Saving your progress...",
  "Adding this to your plan...",
  "Building your plan...",
  "Personalizing your experience...",
  "Updating your plan...",
  "Mapping this to your profile...",
  "Getting things ready for you...",
  "Noting your preferences...",
  "Connecting the dots...",
  "Tailoring your plan...",
  "Adjusting to your needs...",
  "Learning what works for you...",
] as const;

/** Field-name to contextual status text. Plain text — rendered in a span. */
const FIELD_STATUS_MAP: Record<string, string> = {
  primary_concern: "Noting your primary concern...",
  weight: "Adding to your biometrics...",
  current_weight: "Adding to your biometrics...",
  goal_weight: "Adding to your biometrics...",
  height: "Adding to your biometrics...",
  height_inches: "Adding to your biometrics...",
  bmi: "Adding to your biometrics...",
  medications: "Noting your medications...",
  current_medications: "Noting your medications...",
  conditions: "Recording your health history...",
  medical_conditions: "Recording your health history...",
  health_conditions: "Recording your health history...",
  motivation_trigger: "Understanding your goals...",
  goals: "Understanding your goals...",
  goal: "Understanding your goals...",
  state: "Saving your details...",
  dob: "Saving your details...",
  date_of_birth: "Saving your details...",
  sex: "Saving your details...",
  biological_sex: "Saving your details...",
};

/** Tool-specific status text for non-field-save server tools. */
const TOOL_STATUS_MAP: Record<string, { active: string; complete: string }> = {
  check_eligibility: {
    active: "Checking eligibility...",
    complete: "Eligibility reviewed",
  },
  search_catalog: {
    active: "Looking up options...",
    complete: "Options found",
  },
  get_filtered_offers: {
    active: "Finding personalized options...",
    complete: "Options ready",
  },
  visit: {
    active: "Updating your visit...",
    complete: "Visit updated",
  },
  get_patient: {
    active: "Loading your profile...",
    complete: "Profile loaded",
  },
  save_memory: {
    active: "Remembering this...",
    complete: "Noted",
  },
  search_memory: {
    active: "Checking what I know...",
    complete: "Memory checked",
  },
};

// `VISIBLE_TOOLS` and `SLOW_TOOLS` previously lived here. Both are now
// expressed as per-tool flags on `CHAT_TOOL_CONFIG` in
// `chat/chat-tool-config.ts` (`visibleStatus` and `slowEscalation`).
// Consumers should use `hasVisibleStatus(tool)` / `hasSlowEscalation(tool)`.

/** Escalation thresholds for slow tools. */
export const ESCALATION = {
  PHASE_2_MS: 1500,
  PHASE_2_TEXT: "Taking a closer look...",
  PHASE_3_MS: 3000,
  PHASE_3_TEXT: "Almost there...",
} as const;

/** Pick a random item from the warm copy pool (deterministic per toolCallId). */
export function pickWarmCopy(toolCallId: string): string {
  let hash = 0;
  for (let i = 0; i < toolCallId.length; i++) {
    hash = (hash * 31 + toolCallId.charCodeAt(i)) | 0;
  }
  return WARM_COPY_POOL[Math.abs(hash) % WARM_COPY_POOL.length];
}

/** Get field-aware status text for save_intake_fields. */
export function getFieldStatus(fieldName: string | undefined): string {
  if (!fieldName) return pickWarmCopy(String(Date.now()));
  return FIELD_STATUS_MAP[fieldName] ?? pickWarmCopy(fieldName);
}

/** Get tool-specific status and completion text. */
export function getToolStatus(
  toolName: string,
  input?: Record<string, unknown>,
): { active: string; complete: string } | undefined {
  if (toolName === "visit") {
    const action = input?.action as string | undefined;
    if (action === "start") {
      return { active: "Starting your visit...", complete: "Visit started" };
    }
    if (action === "finalize") {
      return { active: "Finalizing your order...", complete: "Order finalized" };
    }
    if (action === "submit") {
      return { active: "Submitting your visit...", complete: "Visit submitted" };
    }
    if (action === "status") {
      return { active: "Checking visit status...", complete: "Status updated" };
    }
  }
  return TOOL_STATUS_MAP[toolName];
}

/** Format a field value for display as a fact line. */
export function formatFieldValue(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "string") {
    if (!value.trim()) return null;
    // Title-case and clean up snake_case
    return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    const formatted = value.map((v) => formatFieldValue(v)).filter(Boolean);
    return formatted.length > 0 ? formatted.join(", ") : null;
  }
  return String(value);
}

/** Human-readable labels for field names that don't self-describe when title-cased. */
const FIELD_LABEL_MAP: Record<string, string> = {
  sex_no_fu: "Biological Sex",
  biological_sex: "Biological Sex",
  sex_at_birth: "Sex at Birth",
  dob: "Date of Birth",
  date_of_birth: "Date of Birth",
  bmi: "BMI",
  primary_concern: "Primary Concern",
};

/** Format a field name into a human-readable label for completion text. */
export function formatFieldLabel(fieldName: string): string {
  return (
    FIELD_LABEL_MAP[fieldName] ??
    fieldName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
